import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { useLoad } from "../../../shared/loading/useLoad";
import { resolveModelVersion } from "../modelVersions";
import { ONTOLOGY_SLICE_PARAMETER, parseSliceAddress, readSliceLens } from "../slice/sliceLens";
import OntologyVersionView from "../OntologyVersionView";
import { readOntologyView, withOntologyView, type OntologySelection } from "../ontologySelection";
import { useDataModel } from "./dataModelContext";

/**
 * The terms of the project ontology at the chosen model version, structured or as Turtle
 * (FR-MODEL-1): the Data model page itself (F77), at `/data-model` (the latest) and
 * `/data-model/<commit>/terms/<term IRI>`. The header names the version. Import and "Push to
 * GitHub" are gone (UX-13): changes are proposed as change sets and reviewed. With
 * `?slice=<thread>.<entry>`, only a Thread's ontology slice shows (F95).
 */
export default function TermsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { termIri } = useParams();
  const [query] = useSearchParams();
  const { projectId, versions, reloadVersions, chosenVersion, failingTerms } = useDataModel();
  const sliceAddress = query.get(ONTOLOGY_SLICE_PARAMETER);
  const loadSlice = useCallback((signal: AbortSignal) => {
    const address = parseSliceAddress(sliceAddress);
    return address === null ? Promise.resolve(null) : readSliceLens(address, signal);
  }, [sliceAddress]);
  const { state: slice, reload: reloadSlice } = useLoad(loadSlice);

  if (versions.status !== "loaded") {
    return <LoadStatus state={versions} loadingLabel={t("ontologyViewer.loadingVersions")} loadingClassName="py-10" onRetry={reloadVersions} />;
  }
  if (slice.status !== "loaded") {
    return <LoadStatus state={slice} loadingLabel={t("ontologyViewer.slice.loading")} loadingClassName="py-10" onRetry={reloadSlice} />;
  }
  if (sliceAddress !== null && slice.value === null) {
    return <StatusMessage tone="info" title={t("ontologyViewer.slice.missingTitle")}>{t("ontologyViewer.slice.missingBody")}</StatusMessage>;
  }
  const resolved = resolveModelVersion(versions.value, chosenVersion);
  if (resolved === null) {
    return (
      <StatusMessage tone="info" title={t("ontologyViewer.noVersionTitle")}>
        {t("ontologyViewer.noVersionBody")}
      </StatusMessage>
    );
  }
  const selection: OntologySelection = { version: chosenVersion, termIri: termIri ?? null, ...readOntologyView(query) };
  // Every choice pins the version shown, so the address names an exact version (NFR-16).
  const handleSelectionChange = (next: OntologySelection) => {
    const path = next.version === null ? appPaths.ontology() : appPaths.ontology({ version: next.version, termIri: next.termIri });
    const nextQuery = withOntologyView(query, next).toString();
    void navigate(nextQuery === "" ? path : `${path}?${nextQuery}`);
  };
  return (
    <OntologyVersionView
      projectId={projectId}
      commit={resolved.commit}
      isLatest={resolved.isLatest}
      selection={selection}
      onSelectionChange={handleSelectionChange}
      failingTerms={failingTerms}
      slice={slice.value}
    />
  );
}
